import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, Button, FormControl, InputLabel, Select, SelectChangeEvent, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import isEqual from 'lodash.isequal';
import Papa, { ParseResult } from 'papaparse';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClaimData, InsurancePeriod } from '../../../backendsdk';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { ResponsiveDatePicker } from '../../../components/MuiDatePicker';
import { TrackedLink as Link, TrackedMenuItem as MenuItem } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';

interface ClaimDataInput {
    Insurer: string;
    'Claim Number': string;
    'Sub Claim Number': number;
    'Claim Type': string;
    'Loss Date': string;
    'Date Received': string;
    Paid: string;
    Reserve: string;
    Expenses: string;
    Salvage: string;
    Recovery: string;
    Total: string;
    'Claim Status': string;
}

const LOSS_RUNS_COLUMNS = [
    'insurer',
    'claim_number',
    'sub_claim_number',
    'claim_type',
    'loss_date',
    'date_received',
    'paid',
    'reserve',
    'expenses',
    'salvage',
    'recovery',
    'total',
    'claim_status',
] as const;

const LOSS_RUNS_COLUMN_MAP: Record<string, typeof LOSS_RUNS_COLUMNS[number]> = {
    Insurer: 'insurer',
    'Claim Number': 'claim_number',
    'Sub Claim Number': 'sub_claim_number',
    'Claim Type': 'claim_type',
    'Loss Date': 'loss_date',
    'Date Received': 'date_received',
    Paid: 'paid',
    Reserve: 'reserve',
    Expenses: 'expenses',
    Salvage: 'salvage',
    Recovery: 'recovery',
    Total: 'total',
    'Claim Status': 'claim_status',
};

const parseValue = (key: string, value: string, rowNumber: number) => {
    const trimmedValue = value.trim();
    if (!value) {
        if (['paid', 'reserve', 'expenses', 'salvage', 'recovery', 'total'].includes(key)) {
            return [key, 0];
        }
        if (['date_received'].includes(key)) {
            // received date is not always present, so we use today's date as default
            return [key, dayjs().format('MM/DD/YYYY')];
        }
        throw new Error('missing_value', { cause: { row: rowNumber } });
    }
    if (['loss_date', 'date_received'].includes(key)) {
        // make sure date is in MM/DD/YY format
        const date = dayjs(trimmedValue, 'MM/DD/YYYY', true);
        if (!date.isValid()) {
            throw new Error('invalid_date_format', { cause: { row: rowNumber } });
        }
        return [key, date.format('MM/DD/YYYY')];
    }
    if (['paid', 'reserve', 'expenses', 'salvage', 'recovery', 'total'].includes(key)) {
        // remove non-numeric characters except for decimal point
        const parsedValue = parseFloat(trimmedValue.replace(/[^0-9.]/g, ''));
        if (isNaN(parsedValue)) {
            throw new Error('invalid_currency_format', { cause: { row: rowNumber } });
        }
        return [key, parseFloat(trimmedValue.replace(/[^0-9.]/g, ''))];
    }
    return [key, trimmedValue];
};

const onDownloadTemplate = () => {
    const BOM = '\uFEFF';
    const csvContent = [
        'Insurer,Claim Number,Sub Claim Number,Claim Type,Loss Date,Date Received,Paid,Reserve,Expenses,Salvage,Recovery,Total,Claim Status\n',
        'Liberty,505723770,01,BA,2020-03-20,2020-03-30,4857,0,0,0,0,4857,C',
    ].join('');
    const file = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const element = document.createElement('a');
    element.href = URL.createObjectURL(file);
    element.download = 'template.csv';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

interface UploadLossRunsProps {
    periods: InsurancePeriod[];
    switchTab: CallableFunction;
    setAlert: CallableFunction;
}

const UploadLossRuns: React.FC<UploadLossRunsProps> = (props: UploadLossRunsProps) => {
    const [dragAndDropHover, setDragAndDropHover] = useState<boolean>(false);
    const [file, setFile] = useState<File>();
    const [claimsData, setClaimsData] = useState<ClaimData[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [lossRunsDate, setLossRunsDate] = useState<Dayjs | null>(dayjs());
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [claimsToDelete, setClaimsToDelete] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sourceFiles, setSourceFiles] = useState<File[]>([]);
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();
    const { agencyApi } = useApi();

    const confirmDeleteKey = claimsToDelete > 1 ? 'plural' : 'singular';

    const handleUpload = (commitChanges: boolean) => {
        if (lossRunsDate && !!selectedPeriodId) {
            setIsLoading(true);
            agencyApi
                .agencyV0ClaimPost({
                    loadClaimsRequest: {
                        claims_data: claimsData.map((claim) => ({
                            ...claim,
                            loss_runs_date: lossRunsDate.format('YYYY-MM-DD'),
                            period_id: parseInt(selectedPeriodId),
                        })),
                        commit_changes: commitChanges,
                    },
                })
                .then(async (res) => {
                    const showSuccessAlert = () => {
                        props.setAlert({
                            message: t('content.loss_runs.upload_success', {
                                count: res.data.claims_updated.length,
                            }),
                            type: 'success',
                            duration: 6000,
                        });
                    };
                    setClaimsToDelete(res.data.claims_to_delete.length);
                    if (res.data.claims_to_delete.length > 0) {
                        if (commitChanges) {
                            await uploadFiles();
                            showSuccessAlert();
                            setIsDeleteModalOpen(false);
                        } else {
                            setIsDeleteModalOpen(true);
                        }
                        setIsLoading(false);
                    } else {
                        if (commitChanges) {
                            await uploadFiles();
                            showSuccessAlert();
                            setIsLoading(false);
                        } else {
                            handleUpload(true);
                        }
                    }
                })
                .catch(() => {
                    props.setAlert({
                        message: t('content.loss_runs.upload_error'),
                        type: 'error',
                        duration: 6000,
                    });
                    setIsLoading(false);
                });
        }
    };

    const uploadFiles = async () => {
        if (file) {
            await uploadSingleFile(file, 'loss_runs.csv');
            for (const file of sourceFiles) {
                await uploadSingleFile(file);
            }
        }
    };

    const uploadSingleFile = async (file: File, fileName?: string): Promise<boolean> => {
        // request backend for s3 upload url
        if (!selectedPeriodId || !lossRunsDate) {
            return false;
        }

        const post_res = await agencyApi
            .agencyV0ClaimUploadPost({
                claimFileRequest: {
                    file_name: fileName || file.name,
                    period_id: parseInt(selectedPeriodId),
                    loss_runs_date: lossRunsDate.format('YYYY-MM-DD'),
                },
            })
            .catch((err) => props.setAlert({ message: `Failed to get upload URL: ${err}` }));
        if (!post_res) {
            return false;
        }

        const config = {
            headers: {
                'Content-Type': file.type,
            },
        };

        if (post_res.data.upload_url) {
            // upload file to s3
            const put_file_res = await axios
                .put(post_res.data.upload_url, file, config)
                .catch((err) => props.setAlert({ message: `Failed to upload file: ${err}` }));
            if (!put_file_res) {
                return false;
            }
        }

        return true;
    };

    const handleSourceFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const files = Array.from(e.target.files as FileList);
            setSourceFiles(files);
        }
    };

    useEffect(() => {
        setErrorMessage('');
        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results: ParseResult<ClaimDataInput>) => {
                    const res = [];
                    if (!isEqual(Object.keys(LOSS_RUNS_COLUMN_MAP), results.meta.fields)) {
                        setErrorMessage(t('content.loss_runs.invalid_structure'));
                        return;
                    }
                    try {
                        for (let i = 0; i < results.data.length; i++) {
                            const row = results.data[i];
                            const rowData = Object.fromEntries(
                                Object.entries(row).map(([key, value]) =>
                                    parseValue(LOSS_RUNS_COLUMN_MAP[key], value, i),
                                ),
                            );
                            res.push(rowData as ClaimData);
                        }
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    } catch (error: any) {
                        if (error.message === 'invalid_structure') {
                            setErrorMessage(t('content.loss_runs.invalid_structure'));
                        } else if (error.message === 'missing_value') {
                            setErrorMessage(t('content.loss_runs.missing_value', { row: error.cause.row + 1 }));
                        } else if (error.message === 'invalid_date_format') {
                            setErrorMessage(t('content.loss_runs.invalid_date_format', { row: error.cause.row + 1 }));
                        } else if (error.message === 'invalid_currency_format') {
                            setErrorMessage(
                                t('content.loss_runs.invalid_currency_format', { row: error.cause.row + 1 }),
                            );
                        } else {
                            setErrorMessage(t('content.loss_runs.parsing_error'));
                        }
                    }
                    setClaimsData(res);
                },
            });
        } else {
            setClaimsData([]);
        }
    }, [file]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                maxWidth: '600px',
                p: 2,
            }}
        >
            {isDeleteModalOpen ? (
                <ConfirmDialog
                    isDialogOpen={isDeleteModalOpen}
                    isLoading={isLoading}
                    headerText={t(`content.loss_runs.confirm_delete_${confirmDeleteKey}_header`)}
                    bodyText={[
                        t(`content.loss_runs.confirm_delete_${confirmDeleteKey}_1`, { count: claimsToDelete }),
                        t(`content.loss_runs.confirm_delete_${confirmDeleteKey}_2`),
                    ]}
                    buttonText={t('content.coach.details.remove_session.button')}
                    buttonColor="redColor"
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={() => handleUpload(true)}
                />
            ) : null}
            <Box sx={{ display: 'flex', width: '100%' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.languages[0]}>
                    <ResponsiveDatePicker
                        id="loss-runs-date-field"
                        label={t('content.loss_runs.loss_runs_date')}
                        slotProps={{ textField: { size: 'small' } }}
                        value={lossRunsDate}
                        onChange={(value: Dayjs | null) => {
                            setLossRunsDate(value);
                        }}
                        sx={{ width: '175px', mr: 1, flexShrink: 0 }}
                    />
                </LocalizationProvider>
                <FormControl fullWidth>
                    <InputLabel size="small" id="period-field-label">
                        {t('content.loss_runs.insurance_period')}
                    </InputLabel>
                    <Select
                        id="period-field"
                        labelId="period-field-label"
                        fullWidth
                        size="small"
                        label={t('content.loss_runs.insurance_period')}
                        value={selectedPeriodId}
                        onChange={(e: SelectChangeEvent) => setSelectedPeriodId(e.target.value as string)}
                    >
                        {props.periods.map((period) => (
                            <MenuItem key={period.id} value={period.id} id={`period-${period.id}`}>
                                {`${dayjs(period.start_date).format(profile.dateFormat)}–${dayjs(
                                    period.end_date,
                                ).format(profile.dateFormat)} | ${period.coverage_type
                                    .split(',')
                                    .map((coverType) =>
                                        t(`content.loss_runs.period_table.${coverType.toLocaleLowerCase()}`),
                                    )
                                    .join(', ')} | ${period.carrier} | ${period.policy_number}`}
                            </MenuItem>
                        ))}
                        <MenuItem
                            id="new-period"
                            value={'new'}
                            sx={{ color: palette.neutral[500] }}
                            onClick={() => props.switchTab()}
                        >
                            {`${t('content.loss_runs.add_period')}`}
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Box
                onDragOver={(e: React.DragEvent) => {
                    // Prevent default behavior (Prevent file from being opened)
                    e.preventDefault();
                    setDragAndDropHover(true);
                }}
                onDragEnter={() => {
                    setDragAndDropHover(true);
                }}
                onDrop={(e: React.DragEvent) => {
                    // Prevent default behavior (Prevent file from being opened)
                    e.preventDefault();
                    if (e.dataTransfer.files.length > 0) {
                        setFile(e.dataTransfer.files[0]);
                    }
                    setDragAndDropHover(false);
                }}
                onDragLeave={() => {
                    setDragAndDropHover(false);
                }}
                onDragEnd={() => {
                    setDragAndDropHover(false);
                }}
                onDragExit={() => {
                    setDragAndDropHover(false);
                }}
                sx={{ width: '100%', height: '200px', my: 1.5 }}
            >
                <Box
                    sx={{
                        p: 1,
                        border: file === undefined ? '2px dashed var(--gray-color)' : '1px solid var(--gray-color)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {file === undefined ? (
                        <>
                            <FileUploadIcon
                                color={dragAndDropHover ? 'secondary' : 'primary'}
                                fontSize="large"
                                sx={{ mb: 0.5, transition: 'color 0.2s' }}
                            />
                            <Box sx={{ display: 'flex' }}>
                                <Link
                                    id="link-upload-file"
                                    data-testid="link-upload-file"
                                    variant="overline"
                                    component="label"
                                    sx={{ mr: 0.5, cursor: 'pointer', lineHeight: 1 }}
                                >
                                    {t('content.loss_runs.upload_file')}
                                    <input
                                        id="file-field"
                                        data-testid="file-field"
                                        hidden
                                        type="file"
                                        accept=".csv"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                setFile(e.target.files[0]);
                                            }
                                        }}
                                    />
                                </Link>
                                <Typography color="primary" variant="overline" sx={{ lineHeight: 1 }}>
                                    {t('content.loss_runs.or_drop_here')}
                                </Typography>
                            </Box>
                            <Link
                                id="download-template-link"
                                component="span"
                                variant="overline"
                                onClick={() => onDownloadTemplate()}
                                sx={{ cursor: 'pointer', lineHeight: 1, mt: 1 }}
                            >
                                {t('content.loss_runs.download_template')}
                            </Link>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 14, mb: 0.5 }}>{file.name}</Typography>
                            <Link
                                id="clear-file-link"
                                component="span"
                                variant="overline"
                                disabled={isLoading}
                                onClick={() => setFile(undefined)}
                                sx={{ lineHeight: 1, cursor: 'pointer' }}
                            >
                                {t('content.loss_runs.clear')}
                            </Link>
                        </Box>
                    )}
                </Box>
            </Box>
            {errorMessage ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                </Box>
            ) : null}
            <Typography variant="overline">{`${t('content.loss_runs.source_files')}:`}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Button
                    id="btn-browse-files"
                    variant={sourceFiles.length > 0 ? 'contained' : 'outlined'}
                    component="label"
                    sx={{ mr: 1 }}
                >
                    {t('content.loss_runs.browse')}
                    <input id="file-btn" hidden multiple type="file" onChange={handleSourceFileChange} />
                </Button>
                {sourceFiles.length > 0 ? (
                    <Typography variant="overline">
                        {t('content.loss_runs.files', { count: sourceFiles.length })}
                    </Typography>
                ) : null}
            </Box>
            <Button
                id="btn-submit"
                data-testid="btn-submit"
                variant="contained"
                color="secondary"
                disabled={
                    !lossRunsDate ||
                    !selectedPeriodId ||
                    !lossRunsDate.isValid() ||
                    claimsData.length === 0 ||
                    !!errorMessage ||
                    sourceFiles.length === 0 ||
                    isLoading
                }
                onClick={() => handleUpload(false)}
            >
                {t('content.loss_runs.submit')}
            </Button>
        </Box>
    );
};

export default UploadLossRuns;
